import { asJsonObjectValueOrNull } from "@/types/json"
import { validateJsonTemplatePlaceholder } from "@/utils/validation/jsonTemplateValidation"

const AUDIO_DATA_PLACEHOLDER = "{{audio_data}}"

/**
 * Validate that a string is a valid JSON object (not array, not primitive).
 * Returns null if valid or empty, otherwise an error message.
 */
export function validateJsonObject(value: string): string | null {
  if (value.trim() === "") {
    return null
  }

  try {
    const parsed: unknown = JSON.parse(value)
    if (!asJsonObjectValueOrNull(parsed)) {
      return "Must be a JSON object"
    }
    return null
  } catch {
    return "Invalid JSON format"
  }
}

/**
 * Validate an audio template JSON string.
 * Must be an object containing exactly one {{audio_data}} occurrence total,
 * with that occurrence inside a JSON value, or the raw audio placeholder alone.
 * Returns null if valid or empty, otherwise an error message.
 */
export function validateAudioTemplate(value: string): string | null {
  if (value.trim() === "") {
    return null
  }

  const validationError = validateJsonTemplatePlaceholder(
    value,
    AUDIO_DATA_PLACEHOLDER,
    { allowBarePlaceholder: true, requireObject: true },
  )
  if (validationError === "invalid_json") {
    return "Invalid JSON template format"
  }
  if (validationError === "invalid_root") {
    return "Template must be a JSON object or the bare {{audio_data}} placeholder"
  }
  if (validationError !== null) {
    return "Template must contain exactly one {{audio_data}} occurrence total, and it must be inside a JSON value"
  }
  return null
}

/**
 * Validate a media template JSON string.
 * Must contain {{media_data}} placeholder and be valid JSON when placeholders are substituted.
 * Returns null if valid or empty, otherwise an error message.
 */
export function validateMediaTemplate(value: string): string | null {
  if (value.trim() === "") {
    return null
  }

  if (!value.includes("{{media_data}}")) {
    return "Template must contain {{media_data}} placeholder"
  }

  // Raw binary mode is supported when template is exactly {{media_data}}
  if (value.trim() === "{{media_data}}") {
    return null
  }

  try {
    const withPlaceholders = value
      .replace(/"\{\{[^}]+\}\}"/g, '"PLACEHOLDER"')
      .replace(/\{\{[^}]+\}\}/g, "0")
    JSON.parse(withPlaceholders)
    return null
  } catch {
    return "Invalid JSON template format"
  }
}

/**
 * Validate a DTMF template JSON string.
 * Must contain {{digit}} placeholder and be valid JSON when placeholders are substituted.
 * Returns null if valid or empty, otherwise an error message.
 */
export function validateDtmfTemplate(value: string): string | null {
  if (value.trim() === "") {
    return null
  }

  if (!value.includes("{{digit}}")) {
    return "Template must contain {{digit}} placeholder"
  }

  try {
    const withPlaceholders = value
      .replace(/"\{\{[^}]+\}\}"/g, '"PLACEHOLDER"')
      .replace(/\{\{[^}]+\}\}/g, "0")
    JSON.parse(withPlaceholders)
    return null
  } catch {
    return "Invalid JSON template format"
  }
}

/**
 * Format a JSON string or object for pretty-printed display.
 * Returns the original string if parsing fails, or empty string for falsy input.
 */
export function formatJsonString(
  jsonString: string | object | null | undefined,
): string {
  if (!jsonString) {
    return ""
  }
  if (typeof jsonString === "object") {
    return JSON.stringify(jsonString, null, 2)
  }
  if (jsonString.trim() === "") {
    return ""
  }
  try {
    const parsed: unknown = JSON.parse(jsonString)
    return JSON.stringify(parsed, null, 2)
  } catch {
    return jsonString
  }
}
